import React from 'react';
import { X, CheckCircle2, XCircle, Loader2 } from 'lucide-react';
import { cn } from '@/lib/utils';
import type { IntegrityCheckRun } from '@/hooks/useIntegrityChecks';

interface IntegrityCheckSlideoutProps {
  open: boolean;
  onClose: () => void;
  latest: IntegrityCheckRun | null;
  running: boolean;
}

const GROUPS: Record<string, string[]> = {
  'Extraction Integrity': [
    'voice_unanimous_no_votes',
    'source_span_in_text',
    'financial_amount_reasonable',
    'motion_tally_reasonable',
  ],
  'Pipeline Completeness': [
    'extracted_no_chunks',
    'minutes_without_attendees',
    'jobs_stuck_running',
  ],
  'Schema / Code Drift': [
    'extract_method_enum',
    'job_type_enum',
    'processing_stage_enum',
    'deterministic_subtypes',
    'job_type_handlers',
  ],
  'Source Staleness': ['source_staleness'],
};

export const IntegrityCheckSlideout: React.FC<IntegrityCheckSlideoutProps> = ({
  open,
  onClose,
  latest,
  running,
}) => {
  if (!open) return null;

  const result = latest?.result;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <div className="absolute inset-0 bg-black/30" onClick={onClose} />
      <div className="relative z-50 w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-4 border-b sticky top-0 bg-white z-10">
          <h2 className="text-sm font-semibold text-gray-900">
            Data Integrity Check
          </h2>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-6">
          {running && (
            <div className="flex items-center gap-2 text-blue-600 text-sm">
              <Loader2 className="w-4 h-4 animate-spin" />
              Running integrity checks...
            </div>
          )}

          {latest?.status === 'failed' && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3">
              <p className="text-sm font-medium text-red-700">Check failed</p>
              {latest.error && (
                <p className="text-xs text-red-600 mt-1 font-mono">
                  {latest.error}
                </p>
              )}
            </div>
          )}

          {result && (
            <>
              <div className="flex items-center gap-4">
                <div className="text-center">
                  <p className="text-3xl font-bold text-gray-900">
                    {result.passed}/{result.total}
                  </p>
                  <p className="text-xs text-gray-500">checks passed</p>
                </div>
                <div className="text-xs text-gray-500">
                  {latest?.finished_at && (
                    <p>
                      Completed: {new Date(latest.finished_at).toLocaleString()}
                    </p>
                  )}
                  {latest?.started_at && (
                    <p>
                      Started: {new Date(latest.started_at).toLocaleString()}
                    </p>
                  )}
                </div>
              </div>

              {Object.entries(GROUPS).map(([groupName, checkNames]) => {
                const groupChecks = result.checks.filter((c) =>
                  checkNames.includes(c.name)
                );
                if (groupChecks.length === 0) return null;
                const groupPassed = groupChecks.every((c) => c.pass);
                return (
                  <div key={groupName}>
                    <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wide flex items-center gap-1.5 mb-2">
                      {groupPassed ? (
                        <CheckCircle2 className="w-3.5 h-3.5 text-green-600" />
                      ) : (
                        <XCircle className="w-3.5 h-3.5 text-red-500" />
                      )}
                      {groupName}
                    </h3>
                    <div className="space-y-1">
                      {groupChecks.map((check) => (
                        <div
                          key={check.name}
                          className={cn(
                            'flex items-start gap-2 rounded-lg px-3 py-2 text-xs',
                            check.pass ? 'bg-green-50' : 'bg-red-50'
                          )}
                        >
                          {check.pass ? (
                            <CheckCircle2 className="w-3.5 h-3.5 text-green-600 mt-0.5 flex-shrink-0" />
                          ) : (
                            <XCircle className="w-3.5 h-3.5 text-red-500 mt-0.5 flex-shrink-0" />
                          )}
                          <div>
                            <p
                              className={cn(
                                'font-medium',
                                check.pass ? 'text-green-800' : 'text-red-800'
                              )}
                            >
                              {check.name}
                            </p>
                            <p
                              className={cn(
                                'mt-0.5',
                                check.pass ? 'text-green-600' : 'text-red-600'
                              )}
                            >
                              {check.detail}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                );
              })}
            </>
          )}

          {!running && !result && (
            <p className="text-sm text-gray-500">
              No integrity check results yet. Run a check to see results.
            </p>
          )}
        </div>
      </div>
    </div>
  );
};
